import { dispatchActionItemRefresh, dispatchGoalsRefresh } from 'Hooks/useRefreshEvent';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import { addQuery, addQueryWithReplace } from 'Helpers/Query';
import { buildModalQuery } from 'Consts/ModalQueries';
import modalConsts from 'Consts/ModalNames';
import moment from 'moment';
import fr from 'facet-rest';
import { SimpleActionItemDraftOverride } from 'Resources/SimpleActionItem';

export async function getNextScheduledMeetingResource(householdResource) {
  if (!householdResource) {
    return null;
  }
  try {
    const [nextMeeting] = await householdResource.relationship('nextMeeting').link('related').get();
    return nextMeeting;
  } catch (err) {
    displayErrorMessage(err, err.errors);
    return null;
  }
}

export async function getMeetingCollection(householdResource) {
  if (!householdResource) {
    return null;
  }
  try {
    return await householdResource.relationship(fr.relationships.householdMeetings.name).link('related').get();
  } catch (err) {
    displayErrorMessage(err, err.errors);
    return null;
  }
}

export async function getActionItemCollection(householdResource) {
  if (!householdResource) {
    return null;
  }
  return householdResource
    .relationship('simpleActionItems')
    .link('related')
    .Get({
      params: { limit: 0, offset: 0 },
    });
}

export async function getGoalCollection(householdResource) {
  if (!householdResource) {
    return null;
  }
  return householdResource
    .relationship('goals')
    .link('related')
    .Get({
      params: { limit: 0, offset: 0 },
    });
}

export function createGoal(text, typeId, goalCollection) {
  if (!goalCollection || !text) {
    return;
  }
  const goalResource = goalCollection.create();
  goalResource
    .attr('name', text)
    .attr('type', typeId)
    .submit()
    .then(() => {
      dispatchGoalsRefresh();
      displaySuccess('Goal created');
    })
    .catch((err) => {
      displayErrorMessage(err, err.errors);
    });
}

export function createActionItemLegacy(text, assignedToHousehold, assignedToId, actionItemCollection, nextScheduledMeetingResource) {
  const actionItemResource = actionItemCollection.create();
  const assignToHouseholdOption = actionItemCollection
    .meta()
    .field('assignedTo')
    .options()
    .list()
    .find(v => v.attr('prompt') === 'Household');
  const assignToMe = actionItemCollection
    .meta()
    .field('assignedTo')
    .options()
    .list()
    .find(v => v.attr('prompt') === 'Me');

  let assignedTo = assignedToHousehold
    ? assignToHouseholdOption.attr('id')
    : assignToMe.attr('id');
  if (assignedToId) {
    assignedTo = assignedToId;
  }
  actionItemResource
    .attr('name', text)
    .attr('dueDate', nextScheduledMeetingResource
      ? moment(nextScheduledMeetingResource.attr('startTime')).toDate().toISOString()
      : moment
        .utc()
        .add(1, 'month')
        .toDate().toISOString())
    .attr('assignedTo', assignedTo)
    .submit()
    .then(() => {
      displaySuccess('To-do created');
      addQueryWithReplace({ refresh: 'action-item' });
      dispatchActionItemRefresh();
    })
    .catch((err) => {
      displayErrorMessage(err, err.errors);
    });
}

export const appendToNoteForMeeting = async (selection, meetingResource) => {
  const [noteResource] = await meetingResource.relationship(fr.relationships.notes.name).link('related').get();

  // append selection to noteResource's contents
  const newContent = `${noteResource.attr('content')}<p>${selection}</p>`;
  noteResource
    .attr('content', newContent)
    .submit()
    .then(() => {
      displaySuccess('Appended to note');
    })
    .catch(err => {
      displayErrorMessage(err, err.errors);
    });
};

export const openActionItemGroupModal = (text, assignedToHousehold, assignedToId, nextScheduledMeetingResource) => {
  let assignedTo;
  if (assignedToId) {
    assignedTo = assignedToId;
  } else if (assignedToHousehold) {
    assignedTo = 'Household';
  } else {
    assignedTo = 'Me';
  }

  let modal;
  try {
    modal = buildModalQuery(modalConsts.simpleActionItemGroupModal, {
      defaultValues: {
        name: text,
        priority: 'medium',
        assignedTo,
        dueDate: nextScheduledMeetingResource
          ? moment(nextScheduledMeetingResource.attr('startTime')).toDate()
          : moment
            .utc()
            .add(1, 'month')
            .toDate(),
      },
    });
  } catch (e) {
    const invalidCharacters = [...text].find(char => char.charCodeAt(0) > 127);
    displayErrorMessage(e, `Text contains invalid character(s): ${invalidCharacters}`);
  }

  if (modal) {
    addQuery({
      modal,
    });
  }
};

export const openActionItemGroupModalAfterCreatingDrafts = async (text, assignedToHousehold, assignedToId, nextScheduledMeetingResource, householdResource) => {
  const dueDate = nextScheduledMeetingResource
    ? moment(nextScheduledMeetingResource.attr('startTime')).toDate()
    : moment.utc().add(1, 'month').toDate();

  const [actionItemDraftGroupResource] = await householdResource.relationship(fr.relationships.actionItemDraftGroup.name).link('related').get();
  const coll = actionItemDraftGroupResource.attr('actionItemDrafts');

  try {
    // Create drafts
    const tasks = text.replace('\xa0', ' ').split('\n').reduce((list, task) => { // \xa0 is a non-breaking space
      const t = task.trim();
      if (t.length > 0) {
        return [...list, t];
      }
      return list;
    }, []);

    for (const task of tasks) {
      const draft = coll.create({
        metaOverrideSet: SimpleActionItemDraftOverride,
      });

      let assignedTo;
      if (assignedToId) {
        assignedTo = assignedToId;
      } else if (assignedToHousehold) {
        // find id
        // don't follow this pattern this should be returned from the server -Kevin
        const options = draft.meta().field('assignedTo').options().list()
          .filter(option => option.attr('group') === 'Household');
        if (options.length > 0) {
          assignedTo = options[0].id().toString();
        }
      } else {
        // find id
        const options = draft.meta().field('assignedTo').options().list()
          .filter(option => option.attr('group') === 'Me');
        if (options.length > 0) {
          assignedTo = options[0].id().toString();
        }
      }

      draft.attr('name', task);
      draft.attr('priority', 'medium');
      draft.attr('assignedTo', assignedTo);
      draft.attr('dueDate', dueDate);
      // eslint-disable-next-line no-await-in-loop
      await draft.submit();
    }

    addQuery({ modal: modalConsts.simpleActionItemGroupModal });
  } catch (e) {
    const invalidCharacters = [...text].find(char => char.charCodeAt(0) > 127);
    displayErrorMessage(e, `Text contains invalid character(s): ${invalidCharacters}`);
  }
};

export const createActionItem = (text, assignedToHousehold, assignedToId, householdResource, actionItemCollection, nextScheduledMeetingResource) => {
  if (!text) {
    return;
  }
  openActionItemGroupModalAfterCreatingDrafts(text, assignedToHousehold, assignedToId, nextScheduledMeetingResource, householdResource);
};
